import React, { useEffect, useState } from 'react';
import { db } from '../../firebase';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import './Resumecommande.css';
import Footer from '../Footer';
import { MdTableBar } from "react-icons/md";
import { 
  FaReceipt, 
  FaUser,
  FaClock, 
  FaCheckCircle, 
  FaSpinner, 
  FaExclamationTriangle,
  FaShoppingCart,
  FaEuroSign,
  FaPlus,
  FaCreditCard,
  FaMoneyBillWave,
  FaTicketAlt,
  FaUtensils,
  FaCoffee,
  FaIceCream,
  FaHamburger,
  FaPizzaSlice,
  FaWineGlassAlt,
  FaMobileAlt,
  FaHandHoldingUsd
} from 'react-icons/fa';

const ResumeCommande = () => {
  const [articles, setArticles] = useState([]); // État pour les articles regroupés
  const [total, setTotal] = useState(0); // Total général
  const [statut, setStatut] = useState(''); // État pour le statut de la commande
  const navigate = useNavigate();
  const [showPopup, setShowPopup] = useState(false); 
  const userInfo = JSON.parse(localStorage.getItem('userInfo'));
  const [showPaymentMethodSelector, setShowPaymentMethodSelector] = useState(false);
  const [selectedMethods, setSelectedMethods] = useState([]);
  const [comment, setComment] = useState('');

  // Fonction pour obtenir l'icône et la couleur du statut
  const getStatusInfo = (status) => {
    switch (status.toLowerCase()) {
      case 'initialisée':
        return {
          icon: <FaSpinner className="status-icon spinning" />,
          color: '#3b82f6',
          text: 'Initialisée',
          bgColor: '#dbeafe'
        };
      case 'en cours':
        return {
          icon: <FaClock className="status-icon pulsing" />,
          color: '#f59e0b',
          text: 'En cours',
          bgColor: '#fef3c7'
        };
      case 'prête':
        return {
          icon: <FaCheckCircle className="status-icon bouncing" />,
          color: '#10b981',
          text: 'Prête',
          bgColor: '#d1fae5'
        };
      case 'servie':
        return {
          icon: <FaCheckCircle className="status-icon" />,
          color: '#059669',
          text: 'Servie',
          bgColor: '#a7f3d0'
        };
      default:
        return {
          icon: <FaExclamationTriangle className="status-icon" />,
          color: '#6b7280',
          text: status,
          bgColor: '#f3f4f6'
        };
    }
  };

  // Fonction pour obtenir l'icône selon la catégorie de l'article
  const getItemIcon = (item) => {
    // Utiliser la vraie catégorie de l'article si elle existe
    const category = item.categorie || item.category;
    
    if (category) {
      switch (category.toLowerCase()) {
        case 'entrees':
        case 'entrées':
          return <FaUtensils className="item-category-icon entree-icon" />;
        case 'plats':
          return <FaHamburger className="item-category-icon food-icon" />;
        case 'desserts':
          return <FaIceCream className="item-category-icon dessert-icon" />;
        case 'boissons':
          return <FaCoffee className="item-category-icon drink-icon" />;
        default:
          return <FaUtensils className="item-category-icon default-icon" />;
      }
    }
    
    // Fallback : détection par nom si pas de catégorie
    const name = item.nom.toLowerCase();
    
    // Boissons
    if (name.includes('café') || name.includes('thé') || name.includes('jus') || 
        name.includes('boisson') || name.includes('coca') || name.includes('eau') ||
        name.includes('soda') || name.includes('limonade') || name.includes('smoothie')) {
      return <FaCoffee className="item-category-icon drink-icon" />;
    }
    
    // Vins et alcools
    if (name.includes('vin') || name.includes('bière') || name.includes('cocktail') ||
        name.includes('champagne') || name.includes('whisky') || name.includes('rhum')) {
      return <FaWineGlassAlt className="item-category-icon wine-icon" />;
    }
    
    // Desserts
    if (name.includes('dessert') || name.includes('glace') || name.includes('muffin') ||
        name.includes('tiramisu') || name.includes('açaï') || name.includes('tarte') ||
        name.includes('gâteau') || name.includes('crème') || name.includes('chocolat')) {
      return <FaIceCream className="item-category-icon dessert-icon" />;
    }
    
    // Pizzas
    if (name.includes('pizza') || name.includes('margherita') || name.includes('quatre fromages')) {
      return <FaPizzaSlice className="item-category-icon pizza-icon" />;
    }
    
    // Plats principaux
    if (name.includes('burger') || name.includes('sandwich') || name.includes('wrap') ||
        name.includes('salade') || name.includes('pâtes') || name.includes('riz') ||
        name.includes('viande') || name.includes('poisson') || name.includes('poulet')) {
      return <FaHamburger className="item-category-icon food-icon" />;
    }
    
    // Par défaut
    return <FaUtensils className="item-category-icon default-icon" />;
  };

  useEffect(() => {
    // Fonction pour récupérer les commandes
    const fetchCommandes = async () => {
      try {
        const commandeCollection = collection(db, `clients/${userInfo.id}/commande`);
        const q = query(commandeCollection);

        // Utiliser onSnapshot pour écouter les changements en temps réel
        onSnapshot(q, (querySnapshot) => {
          const allCommandes = [];
          querySnapshot.forEach(doc => {
            const data = doc.data();
            allCommandes.push(...data.items);
            setStatut(data.statut); // Récupérer le statut de la commande
          });

          const groupedArticles = allCommandes.reduce((acc, item) => {
            const existingItemIndex = acc.findIndex(
              (existingItem) => existingItem.nom === item.nom
            );

            if (existingItemIndex !== -1) {
              acc[existingItemIndex].quantity += item.quantity;
            } else {
              acc.push({ ...item });
            }

            return acc;
          }, []);

          setArticles(groupedArticles);

          // Stocker les articles dans le localStorage pour la confirmation
          localStorage.setItem('articles', JSON.stringify(groupedArticles));
          
          // Calculer le total général
          const totalAmount = groupedArticles.reduce((acc, item) => acc + item.prix * item.quantity, 0);
          setTotal(totalAmount);          
        });
      } catch (error) {
        console.error("Erreur lors de la récupération des commandes: ", error);
      }
    };

    fetchCommandes();
  }, [userInfo.id]);

  useEffect(() => {
    const fetchCommandes = async () => {
      try {
        const commandeCollection = collection(db, `clients/${userInfo.id}/commande`);
        const q = query(commandeCollection);
  
        onSnapshot(q, (querySnapshot) => {
          let latestCommande = null;
          querySnapshot.forEach(doc => {
            const data = doc.data();
            const commandeDate = data.date.toDate();
  
            if (!latestCommande || commandeDate > latestCommande.date) {
              latestCommande = { ...data, date: commandeDate };
            }
          });
  
          if (latestCommande) {
            setStatut(latestCommande.statut);
          }
        });
      } catch (error) {
        console.error("Erreur lors de la récupération des commandes: ", error);
      }
    };
  
    fetchCommandes();
  }, [userInfo.id]);

  const handleFinishAndPay = () => {
    setShowPopup(true); // Afficher la popup
    // Faire défiler vers la popup qui est au centre
    setTimeout(() => {
      const popup = document.querySelector('.payment-popup');
      if (popup) {
        const popupRect = popup.getBoundingClientRect();
        const scrollTop = window.pageYOffset + popupRect.top - (window.innerHeight / 2) + (popupRect.height / 2);
        window.scrollTo({
          top: Math.max(0, scrollTop),
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const handleClosePopup = () => {
    setShowPopup(false); // Fermer la popup
  };

  const handleContinueOrder = () => {
    navigate('/menu', { state: { from: '/resume' } }); // Naviguer vers le menu pour ajouter plus d'articles
  };

  const handlePayMobile = () => {
    setShowPopup(false);
    navigate('/paiement', { state: { total } }); // Naviguer vers la page de paiement mobile
  };

  const handlePayAtCounter = () => {
    setShowPopup(false);
    setShowPaymentMethodSelector(true); // Affiche le sélecteur de moyen de paiement
    // Faire défiler vers la popup qui est au centre
    setTimeout(() => {
      const popup = document.querySelector('.modal-payment');
      if (popup) {
        const popupRect = popup.getBoundingClientRect();
        const scrollTop = window.pageYOffset + popupRect.top - (window.innerHeight / 2) + (popupRect.height / 2);
        window.scrollTo({
          top: Math.max(0, scrollTop),
          behavior: 'smooth'
        });
      }
    }, 100);
  };
  
  const togglePaymentMethod = (method) => {
    setSelectedMethods(prev =>
      prev.includes(method)
        ? prev.filter(m => m !== method)
        : [...prev, method]
    );
  };
  
  const confirmPaymentMethods = () => {
    const moyenPaiementString = selectedMethods.join(', ');
    localStorage.setItem('moyen_paiement', moyenPaiementString);
    localStorage.setItem('total', total); 
    navigate('/cashpaiement');
  };  

  const statusInfo = getStatusInfo(statut);

  return (
    <div className="resume-container">
      <div className="header-section">
        <div className="title-with-icon">
          <h1>Résumé de votre Commande</h1>
        </div>
        
        <div className="order-number-highlight">
          <FaReceipt className="order-icon" />
          <span className="order-number">#{userInfo.idclient}</span>
        </div>
      </div>

      <div className="order-info-grid">
        <div className="info-card">
          <FaUser className="info-icon" />
          <div className="info-content">
            <span className="info-label">Client</span>
            <span className="info-value">{userInfo.prenom}</span>
          </div>
        </div>

        <div className="info-card">
          <MdTableBar className="info-icon" />
          <div className="info-content">
            <span className="info-label">Table</span>
            <span className="info-value">{userInfo.table === 'a_emporter' ? 'À Emporter' : `Table ${userInfo.table}`}</span>
          </div>
        </div>

        <div className="info-card status-card">
          <div className="status-icon-container" style={{ backgroundColor: statusInfo.bgColor }}>
            {statusInfo.icon}
          </div>
          <div className="info-content">
            <span className="info-label">Statut</span>
            <span className="info-value status-text" style={{ color: statusInfo.color }}>
              {statusInfo.text}
            </span>
          </div>
        </div>
      </div>

      <div className="section-header">
        <FaReceipt className="section-icon" />
        <h2>Ticket de Caisse</h2>
      </div>
      
      <div className="ticket-container">
        <div className="ticket-header">
          <div className="ticket-title">SKANEAT</div>
          <div className="ticket-date">{new Date().toLocaleDateString('fr-FR')}</div>
        </div>
        
        <div className="ticket-items">
          {articles.length > 0 ? (
            articles.map((item, index) => (
              <div key={index} className="ticket-item">
                <div className="ticket-item-line">
                  <div className="item-info">
                    {getItemIcon(item)}
                    <span className="item-name">{item.nom}</span>
                  </div>
                  <span className="item-quantity">x{item.quantity}</span>
                </div>
                <div className="ticket-price-line">
                  <span className="unit-price">{item.prix.toFixed(2)}€</span>
                  <span className="total-price">{(item.prix * item.quantity).toFixed(2)}€</span>
                </div>
              </div>
            ))
          ) : (
            <div className="empty-ticket">
              <FaShoppingCart className="empty-icon" />
              <p>Aucun article</p>
            </div>
          )}
        </div>
        
        <div className="ticket-separator"></div>
        
        <div className="ticket-total">
          <span className="total-label">TOTAL</span>
          <span className="total-amount">{total.toFixed(2)}€</span>
        </div>
        
        <div className="ticket-footer">
          <p>Merci pour votre visite !</p>
        </div>
      </div>

      {/* Champ de commentaire */}
      <div className="comment-section">
        <label htmlFor="order-comment" className="comment-label">
          <FaReceipt className="comment-icon" />
          Commentaire pour la commande (optionnel)
        </label>
        <textarea
          id="order-comment"
          className="comment-input"
          placeholder="Ajoutez un commentaire pour votre commande..."
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows="3"
        />
      </div>

      <div className="buttons-container">
        <button className="btn-primary" onClick={handleContinueOrder}>
          <FaPlus className="btn-icon" />
          Continuer la commande
        </button>
        <button className="btn-secondary" onClick={handleFinishAndPay}>
          <FaEuroSign className="btn-icon" />
          Terminer et payer
        </button>
      </div>
      {showPopup && (
        <div className="payment-popup">
          <div className="popup-content-paiement">
            <h3>Choisissez votre méthode de paiement</h3>
            <div className="payment-options">
              <button className="pay-mobile-btn" onClick={handlePayMobile}>
                <div className="button-content">
                  <FaMobileAlt className="payment-icon-resume" />
                  <span className="button-text">Payer sur mobile</span>
                </div>
              </button>
              <button className="pay-table-btn" onClick={handlePayAtCounter}>
                <div className="button-content">
                  <FaHandHoldingUsd className="payment-icon-resume" />
                  <span className="button-text">Payer à table</span>
                </div>
              </button>
            </div>
            <button className='cancel-button-annuler' onClick={handleClosePopup}>Annuler</button>
          </div>
        </div>
      )}

      {showPaymentMethodSelector && (
        <div className="modal-payment">
          <div className="modal-content-payment">
            <h3>Sélectionnez un ou plusieurs moyens de paiement</h3>
            <div className="payment-total-amount">
              <div className="total-content">
                <span className="payment-total-label">Total général</span>
                <span className="payment-total-value">{total.toFixed(2)} €</span>
              </div>
            </div>
            <div className="payment-methods-group">
              {[
                { name: 'Carte Bleue', icon: FaCreditCard },
                { name: 'Espèces', icon: FaMoneyBillWave },
                { name: 'Ticket Restaurant', icon: FaTicketAlt }
              ].map(({ name, icon: Icon }) => (
                <button
                  key={name}
                  className={`method-button ${selectedMethods.includes(name) ? 'method-button--selected' : ''}`}
                  onClick={() => togglePaymentMethod(name)}
                  type="button"
                >
                  <Icon className="method-icon" />
                  {name}
                </button>
              ))}
            </div>
            <button
              className="payment-confirm-button"
              onClick={confirmPaymentMethods}
              disabled={selectedMethods.length === 0}
            >
              Confirmer
            </button>
            <button className="payment-cancel-button" onClick={() => setShowPaymentMethodSelector(false)}>
              Annuler
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ResumeCommande;
